import React from "react";
import { useState, useRef } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FiSearch } from "react-icons/fi";

import { getUrl, getCompanyLookupUrl } from "../../helpers/url";

import { getAustraliaData } from "../../fetchSearchResults/australia";
import { getDenmarkData } from "../../fetchSearchResults/denmark";
import { getNorwayData } from "../../fetchSearchResults/norway";
import { getSwitzerlandData } from "../../fetchSearchResults/switzerland";
import { getUkData } from "../../fetchSearchResults/uk";

import { useDispatch, useSelector } from "react-redux";
import {
  setShowErrorModal,
  setShowSuccessModal,
} from "../../store/slice/modalSlice";

import { storeSearchWindowInput } from "../../store/slice/searchWindowSlice";

import { labelForVatOption } from "../../pages/SearchWindow";

function SearchWindowResults({ getPlanInfo, selectedCountry, planData }) {
  const dispatch = useDispatch();
  const searchInput = useSelector((state) => state.searchWindow.searchInput);
  const radioFilter = useSelector((state) => state.searchWindow.radioFilter);
  const [dataFetched, setDataFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [storedIndex, setStoredIndex] = useState("");
  const organizationsData = useRef([]);
  const [searchParams] = useSearchParams();

  // This function is responsible for adding organization
  const onAddOrganization = async (organization, index) => {
    try {
      setStoredIndex(index);
      setButtonLoading(true);
      const companyNumber = organization.companyNumber;
      // Getting query parameters by using useSearchParams
      const companyId = searchParams.get("companyId");
      const userId = searchParams.get("userId");
      const orgId = searchParams.get("selectedIds");
      let counTry = "";

      if (selectedCountry === "UK") {
        counTry = "uk";
      } else {
        counTry = selectedCountry[0].toLowerCase() + selectedCountry.slice(1);
      }

      const body = {
        counTry,
        userId,
        companyId,
        orgId,
        companyNumber,
      };

      await axios.post(getUrl("REACT_APP_UPDATE_ORG"), body);
      dispatch(
        setShowSuccessModal({
          message:
            "cmsContent.notifications.searchWindow.successContent.updateOrganization.message",
        })
      );
      getPlanInfo();
      organizationsData.current = [];
      dispatch(storeSearchWindowInput(""));
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
    } finally {
      setButtonLoading(false);
    }
  };

  const onSaveCountryPreference = async (country) => {
    const companyId = searchParams.get("companyId");
    await axios.post(getUrl("REACT_APP_SAVE_COUNTRY"), {
      companyId,
      country,
    });
  };

  const getSearchItems = async () => {
    try {
      setLoading(true);
      setDataFetched(false);
      if (!searchInput || searchInput === undefined)
        throw new Error("Search input should not be empty");
      let temp = searchInput;

      await onSaveCountryPreference(selectedCountry);

      let countryName = "";
      if (selectedCountry === "UK") {
        countryName = "uk";
      } else {
        countryName =
          selectedCountry[0].toLowerCase() + selectedCountry.slice(1);
      }

      if (radioFilter === "search by vat") {
        let { data: searchResults } = await axios.get(
          `${getCompanyLookupUrl(
            "REACT_APP_SEARCH_ORGANIZATIONS"
          )}/${countryName}`,
          {
            params: {
              company_number: temp,
            },
          }
        );
        organizationsData.current = searchResults.data;
        setLoading(false);
        setDataFetched(true);
      } else {
        let data;
        if (!searchInput || searchInput === undefined)
          throw new Error("Search input should not be empty");

        if (selectedCountry === "Australia") {
          data = await getAustraliaData(temp);
        } else if (selectedCountry === "Denmark") {
          data = await getDenmarkData(temp);
        } else if (selectedCountry === "Norway") {
          data = await getNorwayData(temp);
        } else if (selectedCountry === "UK") {
          data = await getUkData(temp);
        } else if (selectedCountry === "Switzerland") {
          data = await getSwitzerlandData(temp);
        }
        organizationsData.current = data;
        if (organizationsData.current.length === 0) {
          throw new Error("No company found with " + searchInput);
        }
        setLoading(false);
        setDataFetched(true);
      }
    } catch (error) {
      let message;
      console.log(error);
      setDataFetched(false);
      setLoading(false);
      if (error.response && error.response.data.message)
        message = error.response.data.message;
      else message = error.message;
      if (message === "data is not iterable")
        message = `No company found with ${searchInput}`;
      dispatch(setShowErrorModal({ message: message }));
    }
  };

  // This function is responsible for triggering getSearchItems() function when user enters input value and clicks on Enter button
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      getSearchItems();
    } else if (e.target.value === "") {
      organizationsData.current = [];
      setDataFetched(false);
    }
  };

  return (
    <>
      <div className="search-window-result-table mx-auto">
        <div className="search-window-input-container">
          <input
            type="search"
            placeholder={
              radioFilter === "search by company"
                ? "Search by company name"
                : `Search by ${labelForVatOption[selectedCountry]}`
            }
            className="form-input search-window-input p-2"
            value={searchInput}
            onChange={(e) => dispatch(storeSearchWindowInput(e.target.value))}
            onKeyDown={(e) => handleKeyDown(e)}
          />
          <button
            className="search-window-search-button"
            onClick={getSearchItems}
          >
            Search
          </button>
          <span className="search-icon-container">
            <FiSearch size={20} onClick={getSearchItems} />
          </span>
        </div>
        <div className="search-window-results-container">
          {loading ? (
            <div className="d-flex flex-column justify-content-center align-items-center p-5">
              <div className="d-flex flex-row justify-content-center align-items-center">
                <Spinner animation="border" variant="success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            </div>
          ) : (
            <>
              {searchInput && searchInput.length > 0 ? (
                <>
                  {radioFilter === "search by company" ? (
                    <>
                      {" "}
                      {dataFetched &&
                        organizationsData.current.map((organization, index) => (
                          // List of companies found by the search results
                          <div
                            className="d-flex flex-row justify-content-between align-item-center mx-3 mx-sm-4 py-3"
                            key={index}
                          >
                            <p className="search-company-data my-auto">
                              <span className="search-company-name">
                                {organization?.companyName}
                              </span>
                              <br></br>
                              <span>{organization?.companyNumber}</span>
                            </p>
                            <button
                              className="search-window-add-btn my-auto"
                              onClick={() =>
                                onAddOrganization(organization, index)
                              }
                            >
                              {buttonLoading && storedIndex === index ? (
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                              ) : (
                                <span>Add</span>
                              )}
                            </button>
                          </div>
                        ))}
                    </>
                  ) : (
                    <>
                      {dataFetched && (
                        <div className="d-flex flex-row justify-content-between align-item-center mx-3 mx-sm-4 py-3">
                          <p className="search-company-data my-auto">
                            <span className="search-company-name">
                              {organizationsData.current.companyName}
                            </span>
                            <br></br>
                            <span>
                              {organizationsData.current.companyNumber}
                            </span>
                          </p>
                          <button
                            className="search-window-add-btn my-auto"
                            onClick={() =>
                              onAddOrganization(organizationsData.current)
                            }
                            disabled={!organizationsData.current.companyNumber}
                          >
                            {buttonLoading ? (
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                              />
                            ) : (
                              <span>Add</span>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div></div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default SearchWindowResults;
